import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, SectionHeader } from "@/components/ui-blocks/Reveal";
import { api, endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Heart, Quote, Sparkles } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
};

export const DonorWall = ({ limit = 9 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(endpoints.donorWall).then((r) => { setData(r); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const donors = (data?.donors || []).slice(0, limit);

  return (
    <section id="donor-wall" className="bg-[hsl(var(--surface-tint))]">
      <div className="container-wide py-16 lg:py-24">
        <SectionHeader
          eyebrow="Donor Wall"
          title="A wall of gratitude."
          lead="Every name here changed a life. Every rupee reached the ground — verified, receipted and audited."
        />
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="donor-wall-loading">
            {[...Array(3)].map((_, i) => (<Skeleton key={i} className="h-36 rounded-xl" />))}
          </div>
        ) : donors.length === 0 ? (
          <Reveal>
            <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] bg-card p-10 text-center" data-testid="donor-wall-empty">
              <Heart className="h-10 w-10 mx-auto text-[hsl(var(--orange))]" />
              <h3 className="heading-3 mt-4">Be the first name on this wall.</h3>
              <p className="text-sm text-[hsl(var(--muted-ink))] mt-2 max-w-md mx-auto">Your donation — big or small — starts a chain of kindness that others will follow.</p>
              <Link to="/donate"><Button data-testid="donor-wall-cta" className="mt-5 rounded-full bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold">Donate Now</Button></Link>
            </div>
          </Reveal>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="donor-wall-grid">
              {donors.map((d, i) => (
                <Reveal key={`${d.paid_at}-${i}`} delay={i * 0.04}>
                  <div className="h-full rounded-xl border border-[hsl(var(--border))] bg-card p-5 caf-shadow relative overflow-hidden" data-testid={`donor-card-${i}`}>
                    <div className="absolute top-0 left-0 h-1 w-16 tricolour-strip-h rounded-br" aria-hidden="true" />
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[hsl(var(--green)/0.12)] text-[hsl(var(--green))] grid place-items-center font-bold">
                          {String(d.display_name || "A").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-[hsl(var(--navy))] text-sm" data-testid={`donor-name-${i}`}>{d.display_name}</div>
                          <div className="text-xs text-[hsl(var(--muted-ink))]">{timeAgo(d.paid_at)} • {d.purpose}</div>
                        </div>
                      </div>
                      <div className="text-[hsl(var(--orange))] font-bold tabular">₹{Number(d.amount_inr).toLocaleString("en-IN")}</div>
                    </div>
                    {d.message && (
                      <div className="mt-3 flex gap-2 text-sm text-[hsl(var(--muted-ink))] italic">
                        <Quote className="h-3.5 w-3.5 shrink-0 mt-1 text-[hsl(var(--green))]" />
                        <span>{d.message}</span>
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <div className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-ink))]"><Sparkles className="h-4 w-4 text-[hsl(var(--orange))]" /> {data.total_paid_count} verified donation{data.total_paid_count === 1 ? "" : "s"} and counting</div>
              <div className="flex gap-3">
                <Link to="/donor-wall"><Button variant="outline" data-testid="donor-wall-view-all" className="rounded-full">View full wall</Button></Link>
                <Link to="/donate"><Button data-testid="donor-wall-join" className="rounded-full bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold">Join the wall <Heart className="h-4 w-4 ml-1.5" /></Button></Link>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
